import React from 'react'
import { LockSimple } from '@phosphor-icons/react'
import { nodeMeta, nodeTextClass, nodeTone, visiblePalette } from '../nodeCatalog'
import { usePlanFeatures } from '@/hooks/usePlanFeatures'

// Pure derivation of a module constant — hoisted so the palette isn't rebuilt
// on every render. Which types are withheld is declared in the catalog.
const PALETTE = visiblePalette()

const WorkflowSidebar: React.FC = () => {
  // Plan-gated node types are offered but not draggable, with the reason on
  // the tile. Hiding them outright would leave somebody wondering where A/B
  // Test went; a lock says what to do about it. Only FULLY gated nodes lock
  // here -- a partially gated one (Collaterals' attach-a-file mode, Team
  // Alert's email channel) has a mode every tier gets, so it stays draggable
  // and the paid option is locked in its config panel instead.
  const { gateForNode } = usePlanFeatures()

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType)
    event.dataTransfer.effectAllowed = 'move'
  }

  return (
    <div className="w-64 bg-card border-r border-border/60 h-full overflow-y-auto">
      <div className="p-5 border-b border-border/50">
        <h3 className="font-emphasis tracking-tight text-foreground">Node Palette</h3>
        <p className="text-xs text-muted-foreground mt-1.5">Drag nodes onto the canvas</p>
      </div>
      <div className="p-4 space-y-5">
        {PALETTE.map((category) => (
          <div key={category.name}>
            <p className="text-[10px] font-emphasis text-muted-foreground/60 uppercase tracking-wider mb-2.5 px-1">
              {category.name}
            </p>
            <div className="space-y-2">
              {category.types.map((type) => {
                const { label, description, icon: Icon } = nodeMeta(type)
                const tone = nodeTone(type)
                const textClass = nodeTextClass(type)
                const gate = gateForNode(type)
                const locked = !!gate && !gate.partial
                return (
                  <div
                    key={type}
                    draggable={!locked}
                    onDragStart={locked ? undefined : (e) => onDragStart(e, type)}
                    aria-disabled={locked || undefined}
                    title={locked ? 'Not included in your plan — compare plans in Billing' : undefined}
                    className={`
                      flex items-center gap-3 p-3 rounded-xl border ${tone.borderIdle}
                      transition-colors duration-150 bg-card
                      ${locked
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-grab active:cursor-grabbing hover:border-border/80 hover:bg-accent/30'}
                    `}
                  >
                    <div className={`w-9 h-9 rounded-xl border ${tone.chip} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${textClass}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${textClass} flex items-center gap-1.5`}>
                        {label}
                        {locked && <LockSimple className="w-3 h-3 text-muted-foreground" aria-label="Requires a plan upgrade" />}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {locked ? 'Requires a plan upgrade' : description}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default WorkflowSidebar
